import { useId } from 'react'
import { SelectionFreezeIcon, SelectionLockIcon } from '@/components/ui/custom-icons'
import { MenuRowShortcutKbd } from '@/components/ui/shortcut-kbd'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  tooltipKbdChipClassName,
} from '@/components/ui/tooltip'
import { formatAriaKeyshortcuts } from '@/lib/keyboard/keyboard-shortcuts'
import { cn } from '@/utils'
import { SELECTION_STATE_BADGE_ATTR } from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import type { SelectionBadgeVariant } from '@/lib/shapes/types/selection-badge-variant'

/**
 * Distance from the frame's top edge to the badge, in the *frame's own* units.
 *
 * Inside the zoom-scaled canvas this is document units, so it scales with zoom for free. A caller
 * rendering the badge in screen space (the visual layer) must multiply it by the canvas zoom itself
 * via `offsetTopPx`, or the badge drifts relative to the object as you zoom.
 */
export const SELECTION_BADGE_TOP_OFFSET = 10

export type LockedSelectionBadgeProps = {
  /** Defaults to `lock`. */
  variant?: SelectionBadgeVariant
  /** Lock badge: same path as Object → Unlock Selected. */
  onUnlockClick?: () => void
  /** Freeze badge: clears freeze on the current selection. */
  onUnfreezeClick?: () => void
  /**
   * Overrides the top offset, for callers positioning against a screen-space frame.
   *
   * Only the offset is configurable — the badge's own 24px size stays fixed, and the `right: -12`
   * inset is half that width (the badge straddles the frame's right edge), so it is tied to the icon
   * rather than to the document and must not scale.
   */
  offsetTopPx?: number
}

const BADGE_CONFIG: Record<
  SelectionBadgeVariant,
  {
    label: string
    outlineClass: string
    shortcutKey?: 'unlock' | 'toggleFreeze'
  }
> = {
  lock: {
    label: 'Unlock',
    outlineClass: 'focus-visible:outline-(--color-page-editor-lock)',
    shortcutKey: 'unlock',
  },
  freeze: {
    label: 'Unfreeze',
    outlineClass: 'focus-visible:outline-(--color-page-editor-freeze)',
    shortcutKey: 'toggleFreeze',
  },
}

export function LockedSelectionBadge({
  variant = 'lock',
  onUnlockClick,
  onUnfreezeClick,
  offsetTopPx = SELECTION_BADGE_TOP_OFFSET,
}: LockedSelectionBadgeProps) {
  const freezeClipPathId = useId()
  const onClick = variant === 'lock' ? onUnlockClick : onUnfreezeClick
  const interactive = Boolean(onClick)
  const { label, outlineClass, shortcutKey } = BADGE_CONFIG[variant]

  const icon =
    variant === 'lock' ? (
      <SelectionLockIcon />
    ) : (
      <SelectionFreezeIcon clipPathId={freezeClipPathId} />
    )

  return (
    <div
      className={cn('absolute', interactive ? 'pointer-events-auto z-1' : 'pointer-events-none')}
      style={{
        width: 24,
        height: 24,
        aspectRatio: '1 / 1',
        top: offsetTopPx,
        right: -12,
      }}
      aria-hidden={interactive ? undefined : true}
    >
      {interactive ? (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                {...{ [SELECTION_STATE_BADGE_ATTR]: '' }}
                data-selection-state-badge-kind={variant}
                className={cn(
                  'inline-flex h-full w-full cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0',
                  'text-inherit focus-visible:outline-2 focus-visible:outline-offset-2',
                  outlineClass
                )}
                aria-label={label}
                aria-keyshortcuts={shortcutKey ? formatAriaKeyshortcuts(shortcutKey) : undefined}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onClick?.()
                }}
              >
                {icon}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={6}>
              <span className="min-w-0 shrink">{label}</span>
              {shortcutKey ? (
                <MenuRowShortcutKbd
                  shortcutKey={shortcutKey}
                  comboIndex={0}
                  chipClassName={tooltipKbdChipClassName}
                  className="text-primary-foreground"
                />
              ) : null}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        icon
      )}
    </div>
  )
}
